import { useGridFilter } from 'ag-grid-react';
import { Button } from '@seeqdev/qomponents';
import { useCallback, useEffect, useRef, useState } from 'react';
import { dateTimeFormatter } from '@/vantage/dateTime.utilities';
import { VantageNames } from '@/vantage/constants';
import { errorToast } from '@/utilities/toast.utilities';
import { useFlux } from '@/core/hooks/useFlux.hook';
import { sqWorkbenchStore } from '@/core/core.stores';
import { useTranslation } from 'react-i18next';
import { useFlatpickr } from '@/datetime/flatpickr/useFlatpickr.hook';
import moment from 'moment-timezone';
import { TIME_FORMAT_NUMERICAL_SHORT } from '@/datetime/dateTime.utilities';
import { momentToTimezoneNaiveDate, timezoneNaiveDateToMoment } from '@/datetime/flatpickr/flatpickrTimezones.util';
import type { IDoesFilterPassParams } from 'ag-grid-community';
import type { JSX } from 'react';
import type { Moment } from 'moment-timezone';
import type { Timezone } from '@/datetime/timezone.service';

enum Operator {
  Before = 'Before',
  After = 'After',
}
const operators = [Operator.Before, Operator.After];

interface Model extends Record<string, unknown> {
  selectedDate: Date | null;
  operator: Operator | null;
  start?: number;
  end?: number;
}

const isModel = (model: unknown): model is Model =>
  typeof model === 'object' &&
  model !== null &&
  'selectedDate' in model &&
  (model.selectedDate instanceof Date || model.selectedDate === null) &&
  'operator' in model &&
  (model.operator === null || operators.includes(model.operator as Operator));

const areModelsEqual = (model1: Model | null, model2: Model | null): boolean =>
  model1 === model2 ||
  (isModel(model1) &&
    isModel(model2) &&
    model1.selectedDate?.valueOf() === model2.selectedDate?.valueOf() &&
    model1.operator === model2.operator);

const dateToStartOfMinuteInSeconds = (date: Moment | Date): number => moment(date).startOf('minute').unix();

const ensureDate = (dateValue: Moment | Date | string | null | undefined, timezone: Timezone): Moment | null =>
  dateValue ? moment(dateValue).tz(timezone.name) : null;

const renderEmptyFooter = () => null;

interface DateColumnFilterProps {
  model: Model | null | undefined;
  onModelChange: (model: Model | null) => void;
  colDef: { field: string };
}

export const DateColumnFilter = ({ model, onModelChange, colDef }: DateColumnFilterProps): JSX.Element => {
  const { timezone } = useFlux(sqWorkbenchStore);
  const [selectedDate, setSelectedDate] = useState(ensureDate(model?.selectedDate, timezone));
  const [operator, setOperator] = useState<Operator | null>(model?.operator || null);
  const { t } = useTranslation();
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const flatpickrAnchorRef = useRef<HTMLDivElement>(null);

  const updateModel = useCallback(
    (selectedDate: Moment | null, operator: Operator | null) => {
      const updatedModel: Model | null =
        selectedDate && operator
          ? {
              selectedDate: momentToTimezoneNaiveDate(selectedDate, timezone),
              operator,
            }
          : null;

      if (updatedModel && selectedDate) {
        const nanoSeconds = selectedDate?.valueOf() * 1e6;
        if (colDef.field === VantageNames.Columns.Start) {
          updatedModel.start = nanoSeconds;
        } else if (colDef.field === VantageNames.Columns.End) {
          updatedModel.end = nanoSeconds;
        } else {
          updatedModel[colDef.field] = nanoSeconds;
        }
      }

      onModelChange(updatedModel);
    },
    [timezone, onModelChange, colDef.field],
  );

  const handleDateChange = (date: Moment | null | undefined) => {
    setSelectedDate(date ?? null);
    if (date && operator) {
      updateModel(date, operator);
    }
  };

  const handleOperatorChange = (operator: Operator | null) => {
    setOperator(operator);
    if (selectedDate && operator) {
      updateModel(selectedDate, operator);
    }
  };

  const handleClear = () => {
    setSelectedDate(null);
    setOperator(null);
    updateModel(null, null);
  };

  useFlatpickr({
    anchorRef: flatpickrAnchorRef,
    inline: true,
    value: selectedDate,
    timezone,
    localizedDateFormat: TIME_FORMAT_NUMERICAL_SHORT,
    isOpen: isPickerOpen,
    renderFooter: renderEmptyFooter,
    enableTime: true,
    enableSeconds: true,
    onDateSelect: handleDateChange,
  });

  useEffect(() => {
    const localModel =
      selectedDate && operator
        ? {
            selectedDate: momentToTimezoneNaiveDate(selectedDate, timezone),
            operator,
          }
        : null;
    if (!areModelsEqual(model ?? null, localModel)) {
      const modelDate = ensureDate(model?.selectedDate, timezone);
      const modelOperator = model?.operator ?? null;
      setSelectedDate(modelDate);
      setOperator(modelOperator);
      updateModel(modelDate, modelOperator);
    }
  }, [model, operator, selectedDate, timezone, updateModel]);

  const doesFilterPass = ({ node }: IDoesFilterPassParams<any>): boolean => {
    if (!model) {
      return true;
    }
    try {
      const dateValue = ensureDate(node.data?.[colDef.field], timezone);
      const dateValueSeconds = dateValue ? dateToStartOfMinuteInSeconds(dateValue) : null;
      const selectedDateSeconds = model?.selectedDate
        ? dateToStartOfMinuteInSeconds(timezoneNaiveDateToMoment(model.selectedDate, timezone))
        : null;

      if (selectedDateSeconds === null || model.operator === null) return true;
      switch (model.operator) {
        case Operator.Before:
          return !!dateValueSeconds && dateValueSeconds < selectedDateSeconds;
        case Operator.After:
          return !!dateValueSeconds && dateValueSeconds >= selectedDateSeconds;
        default:
          throw new Error(`Unexpected operator : ${model.operator}`);
      }
    } catch (error) {
      errorToast({ httpResponseOrError: error });
      return true;
    }
  };

  const afterGuiAttached = () => {
    if (!selectedDate && model?.selectedDate) {
      setSelectedDate(ensureDate(model.selectedDate, timezone));
    }
    setIsPickerOpen(true);
  };

  const afterGuiDetached = () => {
    setIsPickerOpen(false);
  };

  useGridFilter({
    doesFilterPass,
    afterGuiAttached,
    afterGuiDetached,
    getModelAsString: (model: Model) => (model.selectedDate ? dateTimeFormatter(model.selectedDate) : ''),
  });

  const isClearDisabled = !selectedDate && !operator;

  return (
    <div className="tw:flex tw:flex-col tw:m-2 tw:gap-4">
      <div ref={flatpickrAnchorRef} className="date-column-filter_flatpickr" />
      <div className="tw:flex tw:flex-row tw:m-2 tw:gap-4">
        <div className="tw:flex tw:gap-4">
          {operators.map((op) => (
            <Button
              key={op}
              size="sm"
              label={t(op === Operator.Before ? 'AUDIT_TRAIL.BEFORE' : 'AUDIT_TRAIL.AFTER')}
              variant={op === operator ? 'theme' : 'outline'}
              onClick={() => handleOperatorChange(op)}
            />
          ))}
        </div>
        <div className="tw:ml-auto">
          <Button
            size="sm"
            label={t('CLEAR')}
            extraClassNames="btn-seeq tw:self-center"
            variant="theme"
            onClick={handleClear}
            disabled={isClearDisabled}
          />
        </div>
      </div>
    </div>
  );
};

export default DateColumnFilter;
